import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, capitalize, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { ArchiveFile, PartyInvolvedDocuments } from "v2-types/order";
import { FileType } from "views/orders/documents/new_documents/types";
import { convertToBytes, convertUnixTimestamp } from "views/orders/documents/new_documents/utils";

type Props = {
  dataEntry: PartyInvolvedDocuments,
  showBottomBorder: boolean,
  findPartyArchiveByPartyId: (partyId: string) => ArchiveFile[],
  handleDownload: (link: string, fileName: string) => void,
  viewFile: (fileId: string) => void,
  onDeleteFile: (file: FileType) => void,
  documentId: string
}

export const DataRow: FC<Props> = ({
  dataEntry: { _id: dataId, name, kinds },
  showBottomBorder,
  findPartyArchiveByPartyId,
  handleDownload,
  viewFile,
  onDeleteFile,
  documentId
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const description = `${name}(${kinds?.join(",")})`;
  const archiveData = findPartyArchiveByPartyId(dataId);
  return (
    <Box style={{
      display: "flex",
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: isMobile ? 'flex-start' : "space-between",
      borderBottom: showBottomBorder ? "1px solid lightgray" : "none",
      padding: theme.spacing(2, 0),
      gap: isMobile ? theme.spacing(1) : 0,
    }}
    >
      <Box style={{ alignSelf: isMobile ? 'flex-start' : "center", flex: 2, minWidth: 0 }}>
        <Tooltip title={description}>
          <Typography variant="body2" color="primary">
            *
            {description.length > 53 ? description.slice(0, 50).concat("...") : description}
          </Typography>
        </Tooltip>
      </Box>

      <Box
        style={{
          display: "grid",
          gap: isMobile ? theme.spacing(1) : "1rem 0rem",
          gridTemplateColumns: isMobile ? '1fr' : "15rem 15rem 5rem 9rem",
          alignItems: "center",
          flex: isMobile ? 'none' : 1,
          minWidth: isMobile ? 0 : undefined,
          justifyContent: isMobile ? 'stretch' : 'flex-end'
        }}
      >
        {archiveData.length === 0 && (
          <Box>
            <Typography variant="body2" color="primary" style={{ textAlign: "left" }}>{t('documents:pendingToUpload')}</Typography>
          </Box>
        )}
        {archiveData.map(({ _id: fileId, filename, download, size, mtime }) => (
          <Box
            key={fileId}
            style={{
              display: isMobile ? 'grid' : 'contents',
              gridTemplateColumns: isMobile ? '1fr' : undefined,
              gap: isMobile ? theme.spacing(1) : undefined,
              borderBottom: isMobile ? '1px solid lightgray' : undefined,
              paddingBottom: isMobile ? theme.spacing(1) : undefined,
            }}
          >
            <Tooltip title={filename}>
              <Box style={{ justifySelf: "flex-start", display: "flex", alignItems: "center", gap: theme.spacing(0.5), minWidth: 0 }}>
                <FontAwesomeIcon icon="file" />
                <Typography variant="body2" color="primary" style={{ overflowWrap: 'anywhere' }}>{filename.length > 30 ? filename.slice(0, 30).concat("...") : filename}</Typography>
              </Box>
            </Tooltip>
            <Tooltip title={t('documents:lastUpdate')}>
              <Typography variant="body2">{capitalize(convertUnixTimestamp(mtime))}</Typography>
            </Tooltip>
            <Typography variant="body2">{convertToBytes(size)}</Typography>
            <Box style={{ display: "flex", gap: theme.spacing(0.5), justifySelf: isMobile ? "flex-start" : "flex-end" }}>
              <Tooltip title={t('documents:viewFile')}>
                <IconButton
                  size="small"
                  style={{ padding: isMobile ? theme.spacing(1.5) : undefined }}
                  onClick={() => viewFile(fileId)}
                >
                  <FontAwesomeIcon icon="eye" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('documents:download')}>
                <IconButton
                  size="small"
                  style={{ padding: isMobile ? theme.spacing(1.5) : undefined }}
                  onClick={() => handleDownload(download, filename)}
                >
                  <FontAwesomeIcon icon="download" />
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                style={{ padding: isMobile ? theme.spacing(1.5) : undefined }}
                onClick={() => onDeleteFile({ fileId, filename, documentId, partyId: dataId })}
              >
                <FontAwesomeIcon icon="trash" />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
